import { DatePipe } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseService } from '@core/services/core/base.service';
import {
  BankAccountBodyRequest,
  EnabledPolicyToChangeBankAccount,
  PendingBankAccountChange,
  SigningKeyStatus
} from '@models/bank-data';
import { Policy, PolicyTypeInUrlPath } from '@models/policy';
import { ProfileUserInfo } from '@models/profile';
import { createHttpParams } from '@utils/utils';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class BankDataService {
  constructor(
    private http: HttpClient,
    private base: BaseService,
    private datePipe: DatePipe
  ) {}

  getPendingBankDataChange({ policyBaseType, policyNumber }: Policy, params?: HttpParams) {
    const policyPath = PolicyTypeInUrlPath[policyBaseType];
    const headers = this.base.getPrivateOIMHeaders();
    const url = `${this.base.getPrivateOIMBaseUrl()}/${policyPath}/${policyNumber}/pendingBankDataChange`;
    return this.http.get<PendingBankAccountChange>(url, {...headers, params});
  }

  modifyBankAccount(
    newAccount: BankAccountBodyRequest,
    policy: EnabledPolicyToChangeBankAccount,
    params: HttpParams
  ) {
    const policyPathName = PolicyTypeInUrlPath[policy.policyBaseType];
    const policyNumber = policy.policyNumber;
    const url = `${this.base.getPrivateOIMBaseUrl()}/${policyPathName}/${policyNumber}/bankDirectDebit`;
    const headers = this.base.getPrivateOIMHeaders({
      cacheResponse: false,
      clearCache: true
    });
    return this.http.post(url, newAccount, { ...headers, params });
  }

  getSignKeyStatus(params?: HttpParams) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/clients/profile`;
    const headers = this.base.getPrivateOIMHeaders();
    return this.http
      .get<ProfileUserInfo>(url, { ...headers, params })
      .pipe(
        map((user: ProfileUserInfo) => {
          if (!user || !user.signingKeyStatusDescription) {
            throw new Error('no status');
          }
          return {
            dateLastModified: this.datePipe.transform(
              user.signingKeyLastModified,
              'dd/MM/yyyy'
            ),
            status: user.signingKeyStatusDescription
          } as SigningKeyStatus;
        })
      );
  }

  getBankOfficeDirection({ officeId, entityCode }) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/banks/offices/${officeId}`;
    const headers = this.base.getPrivateOIMHeaders();
    const params = createHttpParams({
      entityCode
    });
    return this.http
      .get<{ name: string }>(url, { ...headers, params })
      .pipe(catchError(() => of({})));
  }

  validateIBAN(iban: string) {
    iban = iban.split(' ').join('');
    const url = `${this.base.getPrivateOIMBaseUrl()}/banks/accounts/${iban.substr(
      -20
    )}`;
    const headers = this.base.getPrivateOIMHeaders();
    const params = createHttpParams({
      dc: iban.substr(2, 2)
    });
    return this.http.get<{ dc: string; id: string }>(url, {
      ...headers,
      params
    });
  }
}
